/**
 * Machine à états de la partie — PURE : `step(état, événement) → { état, effets }`.
 * Timers, envois réseau, caméra… sont décrits par des effets, exécutés ailleurs.
 *
 * Symétrique sauf pour les décisions de fin de manche : l'HÔTE arbitre
 * (rires, durée max, pause trop longue, vote) et diffuse un `verdict`.
 * Alternative écartée : arbitrage symétrique après délai, qui diverge dès que
 * la latence dépasse ce délai (chacun croit que l'autre a perdu).
 */
import { throwOutcome, type Played } from '../chifoumi/rules';
import type { CalibrationError } from '../detection/calibration';
import { arbitrate, recordLaugh, type Laughs, type Player } from './arbitration';
import { toLocalTime } from './clock';
import type { IncidentKind } from './incidents';
import {
  PROTOCOL_VERSION,
  type GameKind,
  type GameMode,
  type Message,
  type RoundOutcome,
  type VoteVerdict,
} from './protocol';

export type Role = Player;
export type Side = 'me' | 'them';
export type MyOutcome = 'won' | 'lost' | 'tie' | 'draw';

export const ROUNDS_TO_WIN = 2;

export const TIMINGS = {
  calibrationRetryMs: 2500,
  grabFallbackMs: 8000,
  chantMs: 6000,
  duelMs: 60_000,
  /** Attente, après le premier rire, des rires concurrents (fenêtre d'égalité + latence). */
  settleMs: 650,
  pauseMs: 15_000,
  voteMs: 10_000,
  /** Chifoumi : « chi… fou… » avant le MI. */
  chifoumiChantMs: 3000,
  /** Chifoumi : après le MI, le geste est encore lu pendant ce délai, puis verrouillé. */
  lockMs: 300,
  /** Chifoumi (hôte) : attente du geste de l'ami après le MI, au-delà on rejoue le coup. */
  throwWaitMs: 4000,
  /** Imite ce smiley : décompte (visage au repos mesuré), puis 10 s pour imiter. */
  smileyCountdownMs: 2400,
  imitateMs: 10_000,
  /** Imite ce smiley (hôte) : attente du score de l'ami après les 10 s. */
  imitationWaitMs: 4000,
} as const;

/** Imite ce smiley : manches par match (toutes jouées). */
export const SMILEY_ROUNDS = 3;

export type TimerId =
  | 'calibrationRetry'
  | 'grabFallback'
  | 'chant'
  | 'duel'
  | 'settle'
  | 'pause'
  | 'vote'
  | 'lock'
  | 'throw'
  | 'imitate'
  | 'imitationWait';

export type Phase =
  | { readonly name: 'idle' }
  | { readonly name: 'lobby' }
  | { readonly name: 'connected' }
  | { readonly name: 'calibrating'; readonly error: CalibrationError | null }
  | { readonly name: 'grab'; readonly showButton: boolean }
  | { readonly name: 'chant' }
  | { readonly name: 'duel' }
  /** Chifoumi : le « MI ! », chacun montre son geste. */
  | { readonly name: 'throw' }
  /** Imite ce smiley : 10 s pour imiter (`over` : terminées, scores en cours d'échange). */
  | { readonly name: 'imitate'; readonly over: boolean }
  | { readonly name: 'paused' }
  | { readonly name: 'review'; readonly lostFace: Side }
  | { readonly name: 'result'; readonly outcome: MyOutcome; readonly matchOver: boolean }
  | { readonly name: 'ended'; readonly reason: 'quit' | 'peerLeft' | 'versionMismatch' };

export type PhaseName = Phase['name'];

export interface GameState {
  readonly phase: Phase;
  readonly role: Role;
  /** Barbichette ou chifoumi : choisi par l'hôte, adopté par l'invité. */
  readonly game: GameKind;
  /** Rire ou cligner (barbichette) : choisi par l'hôte, adopté par l'invité. */
  readonly mode: GameMode;
  /** Identifiant de tentative (une manche rejouée en a un nouveau). */
  readonly round: number;
  readonly score: { readonly me: number; readonly them: number };
  /** horloge du pair − horloge locale. */
  readonly clockOffset: number;
  readonly meReady: boolean;
  readonly themReady: boolean;
  /** Dernière tentative pour laquelle chacun est calibré / a attrapé (robuste au désordre). */
  readonly meCalibrated: number;
  readonly themCalibrated: number;
  readonly meGrabbed: number;
  readonly themGrabbed: number;
  /** Hôte : rires de la tentative en cours (horloge de l'hôte). */
  readonly laughs: Laughs;
  readonly settling: boolean;
  readonly myIncident: Exclude<IncidentKind, 'noFace'> | null;
  readonly themPaused: boolean;
  readonly rematch: { readonly me: boolean; readonly them: boolean };
  /** Chifoumi : gestes du coup en cours (`null` : pas encore joué / reçu). */
  readonly throws: { readonly me: Played | null; readonly them: Played | null };
  /** Imite ce smiley : graine du tirage des smileys (celle de l'hôte). */
  readonly seed: number;
  /** Numéro du match (revanches) : change les smileys tirés. */
  readonly match: number;
  /** Manches tranchées dans ce match (une manche rejouée ne compte pas). */
  readonly played: number;
  /** Total des scores d'imitation du match (départage à nombre de manches égal). */
  readonly points: { readonly me: number; readonly them: number };
  /** Meilleurs scores de la manche en cours (`null` : pas encore donné / reçu). */
  readonly imitation: { readonly me: number | null; readonly them: number | null };
}

export type GameEvent =
  | {
      readonly type: 'create';
      readonly role: Role;
      readonly mode?: GameMode;
      readonly game?: GameKind;
      /** Hôte, imite ce smiley : graine du tirage (aléatoire, fournie par l'écran). */
      readonly seed?: number;
    }
  | { readonly type: 'peerConnected' }
  | { readonly type: 'peerLeft' }
  | { readonly type: 'quit' }
  | { readonly type: 'clockSync'; readonly offset: number }
  | { readonly type: 'localReady' }
  | { readonly type: 'calibrationSucceeded' }
  | { readonly type: 'calibrationFailed'; readonly error: CalibrationError }
  | { readonly type: 'localGrab' }
  | { readonly type: 'localLaugh'; readonly t: number; readonly score: number }
  | { readonly type: 'incident'; readonly kind: IncidentKind; readonly t: number }
  | { readonly type: 'incidentCleared' }
  | { readonly type: 'localVote'; readonly verdict: VoteVerdict }
  /** Chifoumi : geste lu par l'écran au verrouillage. */
  | { readonly type: 'localThrow'; readonly gesture: Played }
  /** Imite ce smiley : mon meilleur score tenu, lu par l'écran à la fin des 10 s. */
  | { readonly type: 'localImitation'; readonly score: number }
  | { readonly type: 'nextRound' }
  | { readonly type: 'localRematch' }
  | { readonly type: 'timeout'; readonly timer: TimerId; readonly round: number }
  | { readonly type: 'message'; readonly message: Message };

export type Effect =
  | { readonly type: 'send'; readonly message: Message }
  | {
      readonly type: 'startTimer';
      readonly timer: TimerId;
      readonly ms: number;
      readonly round: number;
    }
  | { readonly type: 'cancelTimer'; readonly timer: TimerId }
  | { readonly type: 'startCalibration' }
  | { readonly type: 'startHandTracking' }
  | { readonly type: 'stopHandTracking' }
  /** Envoyer au pair le replay des ~4 dernières secondes (cas « visage perdu »). */
  | { readonly type: 'sendReplay'; readonly round: number }
  /** Chifoumi : lire le geste tenu au MI (l'écran répond par `localThrow`). */
  | { readonly type: 'lockThrow'; readonly round: number }
  /** Imite ce smiley : fin des 10 s, l'écran répond par `localImitation`. */
  | { readonly type: 'submitImitation'; readonly round: number };

export interface Step {
  readonly state: GameState;
  readonly effects: readonly Effect[];
}

export function createInitialState(): GameState {
  return {
    phase: { name: 'idle' },
    role: 'host',
    game: 'barbichette',
    mode: 'laugh',
    round: 0,
    score: { me: 0, them: 0 },
    clockOffset: 0,
    meReady: false,
    themReady: false,
    meCalibrated: 0,
    themCalibrated: 0,
    meGrabbed: 0,
    themGrabbed: 0,
    laughs: {},
    settling: false,
    myIncident: null,
    themPaused: false,
    rematch: { me: false, them: false },
    throws: NO_THROWS,
    seed: 0,
    match: 0,
    played: 0,
    points: { me: 0, them: 0 },
    imitation: NO_IMITATION,
  };
}

const NO_IMITATION = { me: null, them: null } as const;

const NO_THROWS = { me: null, them: null } as const;

// ---------------------------------------------------------------------------
// Transitions de phase
// ---------------------------------------------------------------------------

/** Phases pendant lesquelles une manche est en jeu (un verdict peut tomber). */
const IN_PLAY: readonly PhaseName[] = ['chant', 'duel', 'throw', 'imitate', 'paused', 'review'];
const LAUGH_ALLOWED: readonly PhaseName[] = ['chant', 'duel'];

const PHASE_TIMERS: Partial<Record<PhaseName, readonly TimerId[]>> = {
  calibrating: ['calibrationRetry'],
  grab: ['grabFallback'],
  chant: ['chant'],
  duel: ['duel'],
  throw: ['lock', 'throw'],
  imitate: ['imitate', 'imitationWait'],
  paused: ['pause'],
  review: ['vote'],
};

/** Phases où la main est suivie : l'engagement (barbichette), du « chi-fou » au MI (chifoumi). */
const HAND_PHASES = (s: GameState): readonly PhaseName[] =>
  s.game === 'chifoumi' ? ['chant', 'throw'] : ['grab'];

const same = (state: GameState): Step => ({ state, effects: [] });
const isHost = (s: GameState): boolean => s.role === 'host';
const isChifoumi = (s: GameState): boolean => s.game === 'chifoumi';
const isSmiley = (s: GameState): boolean => s.game === 'smiley';
const isBarbichette = (s: GameState): boolean => s.game === 'barbichette';
const timer = (s: GameState, id: TimerId, ms: number): Effect => ({
  type: 'startTimer',
  timer: id,
  ms,
  round: s.round,
});

function entryEffects(s: GameState): Effect[] {
  switch (s.phase.name) {
    case 'calibrating':
      return [{ type: 'startCalibration' }];
    case 'grab':
      return [{ type: 'startHandTracking' }, timer(s, 'grabFallback', TIMINGS.grabFallbackMs)];
    case 'chant':
      if (isChifoumi(s))
        return [{ type: 'startHandTracking' }, timer(s, 'chant', TIMINGS.chifoumiChantMs)];
      return [timer(s, 'chant', isSmiley(s) ? TIMINGS.smileyCountdownMs : TIMINGS.chantMs)];
    case 'imitate':
      return [timer(s, 'imitate', TIMINGS.imitateMs)];
    case 'throw':
      return [
        timer(s, 'lock', TIMINGS.lockMs),
        ...(isHost(s) ? [timer(s, 'throw', TIMINGS.throwWaitMs)] : []),
      ];
    case 'duel':
      return isHost(s) ? [timer(s, 'duel', TIMINGS.duelMs)] : [];
    case 'paused':
      return isHost(s) ? [timer(s, 'pause', TIMINGS.pauseMs)] : [];
    case 'review':
      return isHost(s) ? [timer(s, 'vote', TIMINGS.voteMs)] : [];
    default:
      return [];
  }
}

/** Change de phase : annule les timers de l'ancienne, lance ceux de la nouvelle. */
function go(s: GameState, phase: Phase, before: readonly Effect[] = []): Step {
  const from = s.phase.name;
  const exit: Effect[] = [];
  for (const id of PHASE_TIMERS[from] ?? []) exit.push({ type: 'cancelTimer', timer: id });
  const hand = HAND_PHASES(s);
  if (hand.includes(from) && !hand.includes(phase.name)) exit.push({ type: 'stopHandTracking' });
  if (IN_PLAY.includes(from) && !IN_PLAY.includes(phase.name)) {
    exit.push({ type: 'cancelTimer', timer: 'settle' });
  }
  const next = { ...s, phase };
  return { state: next, effects: [...before, ...exit, ...entryEffects(next)] };
}

function startAttempt(s: GameState, before: readonly Effect[] = []): Step {
  return go(
    {
      ...s,
      round: s.round + 1,
      laughs: {},
      settling: false,
      myIncident: null,
      themPaused: false,
      throws: NO_THROWS,
      imitation: NO_IMITATION,
    },
    // Chifoumi et smiley : ni visage à calibrer, ni barbichette à attraper.
    isBarbichette(s) ? { name: 'calibrating', error: null } : { name: 'chant' },
    before,
  );
}

/** Hôte (chifoumi) : les deux gestes sont connus → verdict du coup. */
function decideThrow(s: GameState, before: readonly Effect[] = []): Step {
  const { me, them } = s.throws;
  if (!isHost(s) || s.phase.name !== 'throw' || me === null || them === null)
    return { state: s, effects: before };
  const outcome = throwOutcome(me, them);
  return applyVerdict(s, outcome, [
    ...before,
    { type: 'send', message: { type: 'verdict', round: s.round, outcome } },
  ]);
}

/** Hôte (smiley) : les deux scores sont connus → le meilleur gagne la manche. */
function decideImitation(s: GameState, before: readonly Effect[] = []): Step {
  const { me, them } = s.imitation;
  if (!isHost(s) || s.phase.name !== 'imitate' || me === null || them === null)
    return { state: s, effects: before };
  const outcome: RoundOutcome = me === them ? 'tie' : me > them ? 'guestLost' : 'hostLost';
  return applyVerdict(s, outcome, [
    ...before,
    { type: 'send', message: { type: 'verdict', round: s.round, outcome } },
  ]);
}

/** Applique un verdict (hôte : décidé localement ; invité : reçu). */
function applyVerdict(s: GameState, outcome: RoundOutcome, before: readonly Effect[] = []): Step {
  if (outcome === 'replay') return startAttempt(s, before);
  let mine: MyOutcome;
  if (outcome === 'tie' || outcome === 'draw') mine = outcome;
  else mine = (outcome === 'hostLost') === isHost(s) ? 'lost' : 'won';
  const score = {
    me: s.score.me + (mine === 'won' ? 1 : 0),
    them: s.score.them + (mine === 'lost' ? 1 : 0),
  };
  if (isSmiley(s)) {
    // Les 3 manches sont toujours jouées ; les scores s'additionnent pour départager.
    const played = s.played + 1;
    const points = {
      me: s.points.me + (s.imitation.me ?? 0),
      them: s.points.them + (s.imitation.them ?? 0),
    };
    const matchOver = played >= SMILEY_ROUNDS;
    return go(
      { ...s, score, played, points, settling: false },
      { name: 'result', outcome: mine, matchOver },
      before,
    );
  }
  const matchOver = score.me >= ROUNDS_TO_WIN || score.them >= ROUNDS_TO_WIN;
  return go({ ...s, score, settling: false }, { name: 'result', outcome: mine, matchOver }, before);
}

/** Hôte : tranche la manche et prévient l'invité. */
function decide(s: GameState, outcome: RoundOutcome): Step {
  return applyVerdict(s, outcome, [
    { type: 'send', message: { type: 'verdict', round: s.round, outcome } },
  ]);
}

/** Hôte : enregistre un rire (horloge hôte) et lance l'attente des rires concurrents. */
function recordHostLaugh(s: GameState, player: Player, t: number, before: readonly Effect[]): Step {
  const laughs = recordLaugh(s.laughs, player, t);
  if (laughs === s.laughs && s.settling) return { state: s, effects: before };
  const effects = s.settling ? before : [...before, timer(s, 'settle', TIMINGS.settleMs)];
  return { state: { ...s, laughs, settling: true }, effects };
}

// ---------------------------------------------------------------------------
// Événements
// ---------------------------------------------------------------------------

export function step(s: GameState, event: GameEvent): Step {
  const phase = s.phase.name;
  if (phase === 'ended') return same(s);

  switch (event.type) {
    case 'create':
      return phase === 'idle'
        ? go(
            {
              ...s,
              role: event.role,
              mode: event.mode ?? s.mode,
              game: event.game ?? s.game,
              seed: event.seed ?? s.seed,
            },
            { name: 'lobby' },
          )
        : same(s);

    case 'peerConnected':
      return phase === 'lobby'
        ? go(s, { name: 'connected' }, [
            {
              type: 'send',
              message: {
                type: 'hello',
                version: PROTOCOL_VERSION,
                game: s.game,
                mode: s.mode,
                ...(isSmiley(s) ? { seed: s.seed } : {}),
              },
            },
          ])
        : same(s);

    case 'peerLeft':
      return go(s, { name: 'ended', reason: 'peerLeft' });

    case 'quit':
      return go(s, { name: 'ended', reason: 'quit' });

    case 'clockSync':
      return same({ ...s, clockOffset: event.offset });

    case 'localReady': {
      if (phase !== 'connected' || s.meReady) return same(s);
      const send: Effect = { type: 'send', message: { type: 'ready' } };
      const next = { ...s, meReady: true };
      return next.themReady ? startAttempt(next, [send]) : { state: next, effects: [send] };
    }

    case 'calibrationSucceeded': {
      if (phase !== 'calibrating' || s.meCalibrated === s.round) return same(s);
      const send: Effect = { type: 'send', message: { type: 'calibrated', round: s.round } };
      const next: GameState = {
        ...s,
        meCalibrated: s.round,
        phase: { name: 'calibrating', error: null },
      };
      return next.themCalibrated === s.round
        ? go(next, { name: 'grab', showButton: false }, [send])
        : { state: next, effects: [send] };
    }

    case 'calibrationFailed':
      if (phase !== 'calibrating' || s.meCalibrated === s.round) return same(s);
      return {
        state: { ...s, phase: { name: 'calibrating', error: event.error } },
        effects: [timer(s, 'calibrationRetry', TIMINGS.calibrationRetryMs)],
      };

    case 'localGrab': {
      if (phase !== 'grab' || s.meGrabbed === s.round) return same(s);
      const send: Effect = { type: 'send', message: { type: 'grab', round: s.round } };
      const next = { ...s, meGrabbed: s.round };
      return next.themGrabbed === s.round
        ? go(next, { name: 'chant' }, [send])
        : { state: next, effects: [send] };
    }

    case 'localLaugh': {
      if (!isBarbichette(s) || !LAUGH_ALLOWED.includes(phase)) return same(s);
      const send: Effect = {
        type: 'send',
        message: { type: 'laugh', round: s.round, t: event.t, score: event.score },
      };
      return isHost(s)
        ? recordHostLaugh(s, 'host', event.t, [send])
        : { state: s, effects: [send] };
    }

    case 'incident':
      // Chifoumi : la main peut cacher le visage, et un coup se rejoue de toute façon.
      return isBarbichette(s) ? onIncident(s, event.kind, event.t) : same(s);

    case 'localImitation': {
      const p = s.phase;
      if (p.name !== 'imitate' || !p.over || s.imitation.me !== null) return same(s);
      const score = Math.max(0, Math.min(100, Math.round(event.score)));
      const send: Effect = { type: 'send', message: { type: 'imitation', round: s.round, score } };
      return decideImitation({ ...s, imitation: { ...s.imitation, me: score } }, [send]);
    }

    case 'localThrow': {
      if (phase !== 'throw' || s.throws.me !== null) return same(s);
      const send: Effect = {
        type: 'send',
        message: { type: 'throw', round: s.round, gesture: event.gesture },
      };
      return decideThrow({ ...s, throws: { ...s.throws, me: event.gesture } }, [send]);
    }

    case 'incidentCleared': {
      if (s.myIncident === null) return same(s);
      const send: Effect = { type: 'send', message: { type: 'resume', round: s.round } };
      const next = { ...s, myIncident: null };
      return phase === 'paused' && !next.themPaused
        ? go(next, { name: 'duel' }, [send])
        : { state: next, effects: [send] };
    }

    case 'localVote': {
      if (s.phase.name !== 'review' || s.phase.lostFace !== 'them') return same(s);
      if (isHost(s)) return decide(s, event.verdict === 'defeat' ? 'guestLost' : 'replay');
      return {
        state: s,
        effects: [
          { type: 'send', message: { type: 'vote', round: s.round, verdict: event.verdict } },
        ],
      };
    }

    case 'nextRound':
      return s.phase.name === 'result' && !s.phase.matchOver ? startAttempt(s) : same(s);

    case 'localRematch': {
      if (s.phase.name !== 'result' || !s.phase.matchOver || s.rematch.me) return same(s);
      const send: Effect = { type: 'send', message: { type: 'rematch' } };
      const next = { ...s, rematch: { ...s.rematch, me: true } };
      return next.rematch.them ? startMatch(next, [send]) : { state: next, effects: [send] };
    }

    case 'timeout':
      return event.round === s.round ? onTimeout(s, event.timer) : same(s);

    case 'message':
      return onMessage(s, event.message);
  }
}

function startMatch(s: GameState, before: readonly Effect[] = []): Step {
  return startAttempt(
    {
      ...s,
      score: { me: 0, them: 0 },
      rematch: { me: false, them: false },
      match: s.match + 1,
      played: 0,
      points: { me: 0, them: 0 },
    },
    before,
  );
}

function onIncident(s: GameState, kind: IncidentKind, t: number): Step {
  const phase = s.phase.name;
  if (kind === 'noFace') {
    if (!LAUGH_ALLOWED.includes(phase)) return same(s);
    return go(s, { name: 'review', lostFace: 'me' }, [
      { type: 'send', message: { type: 'faceLost', round: s.round, t } },
      { type: 'sendReplay', round: s.round },
    ]);
  }
  if (!LAUGH_ALLOWED.includes(phase) && phase !== 'paused') return same(s);
  if (s.myIncident === kind) return same(s);
  const effects: Effect[] =
    kind === 'camera' ? [{ type: 'send', message: { type: 'pause', round: s.round } }] : [];
  const next = { ...s, myIncident: kind };
  return phase === 'paused' ? { state: next, effects } : go(next, { name: 'paused' }, effects);
}

function onTimeout(s: GameState, id: TimerId): Step {
  const p = s.phase;
  switch (id) {
    case 'calibrationRetry':
      return p.name === 'calibrating' && p.error !== null
        ? {
            state: { ...s, phase: { name: 'calibrating', error: null } },
            effects: [{ type: 'startCalibration' }],
          }
        : same(s);
    case 'grabFallback':
      return p.name === 'grab' && !p.showButton
        ? same({ ...s, phase: { name: 'grab', showButton: true } })
        : same(s);
    case 'chant':
      if (p.name !== 'chant') return same(s);
      if (isChifoumi(s)) return go(s, { name: 'throw' });
      return go(s, isSmiley(s) ? { name: 'imitate', over: false } : { name: 'duel' });
    case 'imitate':
      // Fin des 10 s : chacun donne son score ; l'hôte attend celui de l'ami.
      return p.name === 'imitate' && !p.over
        ? {
            state: { ...s, phase: { name: 'imitate', over: true } },
            effects: [
              { type: 'submitImitation', round: s.round },
              ...(isHost(s) ? [timer(s, 'imitationWait', TIMINGS.imitationWaitMs)] : []),
            ],
          }
        : same(s);
    case 'imitationWait':
      // Score de l'ami jamais arrivé : on rejoue la manche.
      return isHost(s) && p.name === 'imitate' ? decide(s, 'replay') : same(s);
    case 'lock':
      return p.name === 'throw' && s.throws.me === null
        ? { state: s, effects: [{ type: 'lockThrow', round: s.round }] }
        : same(s);
    case 'throw':
      // Geste de l'ami jamais arrivé : on rejoue le coup.
      return isHost(s) && p.name === 'throw' ? decide(s, 'replay') : same(s);
    case 'duel':
      return isHost(s) && p.name === 'duel' && !s.settling ? decide(s, 'draw') : same(s);
    case 'settle': {
      if (!isHost(s) || !s.settling || !IN_PLAY.includes(p.name)) return same(s);
      return decide(s, arbitrate(s.laughs) ?? 'draw');
    }
    case 'pause':
      return isHost(s) && p.name === 'paused' ? decide(s, 'replay') : same(s);
    case 'vote':
      return isHost(s) && p.name === 'review' ? decide(s, 'replay') : same(s);
  }
}

function onMessage(s: GameState, m: Message): Step {
  const phase = s.phase.name;
  const current = 'round' in m && m.round === s.round;

  switch (m.type) {
    case 'hello':
      if (m.version !== PROTOCOL_VERSION)
        return go(s, { name: 'ended', reason: 'versionMismatch' });
      if (isHost(s)) return same(s);
      return m.mode !== s.mode || m.game !== s.game || (m.seed ?? s.seed) !== s.seed
        ? same({ ...s, mode: m.mode, game: m.game, seed: m.seed ?? s.seed })
        : same(s);

    case 'imitation': {
      if (!isSmiley(s) || !current || s.imitation.them !== null) return same(s);
      return decideImitation({ ...s, imitation: { ...s.imitation, them: m.score } });
    }

    case 'throw': {
      if (!isChifoumi(s) || !current || s.throws.them !== null) return same(s);
      return decideThrow({ ...s, throws: { ...s.throws, them: m.gesture } });
    }

    case 'ready': {
      const next = s.themReady ? s : { ...s, themReady: true };
      return phase === 'connected' && next.meReady ? startAttempt(next) : same(next);
    }

    case 'calibrated': {
      const next = m.round > s.themCalibrated ? { ...s, themCalibrated: m.round } : s;
      return phase === 'calibrating' && current && next.meCalibrated === s.round
        ? go(next, { name: 'grab', showButton: false })
        : same(next);
    }

    case 'grab': {
      const next = m.round > s.themGrabbed ? { ...s, themGrabbed: m.round } : s;
      return phase === 'grab' && current && next.meGrabbed === s.round
        ? go(next, { name: 'chant' })
        : same(next);
    }

    case 'laugh':
      return isHost(s) && current && IN_PLAY.includes(phase)
        ? recordHostLaugh(s, 'guest', toLocalTime(m.t, s.clockOffset), [])
        : same(s);

    case 'faceLost':
      return current && LAUGH_ALLOWED.includes(phase)
        ? go(s, { name: 'review', lostFace: 'them' })
        : same(s);

    case 'pause': {
      if (!current || (!LAUGH_ALLOWED.includes(phase) && phase !== 'paused')) return same(s);
      if (s.themPaused) return same(s);
      const next = { ...s, themPaused: true };
      return phase === 'paused' ? same(next) : go(next, { name: 'paused' });
    }

    case 'resume': {
      if (!current || !s.themPaused) return same(s);
      const next = { ...s, themPaused: false };
      return phase === 'paused' && next.myIncident === null
        ? go(next, { name: 'duel' })
        : same(next);
    }

    case 'vote':
      return isHost(s) && current && s.phase.name === 'review' && s.phase.lostFace === 'me'
        ? decide(s, m.verdict === 'defeat' ? 'hostLost' : 'replay')
        : same(s);

    case 'verdict':
      return !isHost(s) && current && IN_PLAY.includes(phase)
        ? applyVerdict(s, m.outcome)
        : same(s);

    case 'rematch': {
      const next = s.rematch.them ? s : { ...s, rematch: { ...s.rematch, them: true } };
      return s.phase.name === 'result' && s.phase.matchOver && next.rematch.me
        ? startMatch(next)
        : same(next);
    }

    // Gérés par la couche réseau / le ralenti, sans effet sur la partie.
    case 'heartbeat':
    case 'ping':
    case 'pong':
    case 'replayChunk':
    case 'snapshot':
      return same(s);
  }
}
